import { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, Modal, Typography, List, ListItem, ListItemText, IconButton, Snackbar, Alert } from '@mui/material';
import { CloseIcon } from '../../../../../components/Icons/CloseIcon';
import { ArrowBackIcon } from '../../../../../components/Icons/ArrowBackIcon';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';

import styles from './MergeTripsModal.module.scss';
import { Trip } from '../../../../../types/trip.types';
import {
  useGetTripsQuery,
  useMergeTripsMutation,
  usePreviewMergeTripsMutation,
  useUpdateTripStopMutation,
} from '../../../../../store/services/tripsApi';
import CustomInput from '../../../../../common/CustomInput';
import CustomButton from '../../../../../common/CustomButton';
import { SearchIcon } from '../../../../../components/Icons';
import MergeTripCard from './MergeTripCard';
import MergeTripReviewDialog from './MergeTripReviewDialog';
import { MergeTripPreviewResponse } from '../../../../../types/trip.types';
import { mergePreviewHasConflicts } from '../../../../../utils/mergeTripPreview';
import { ActivityType } from '../../../../../types/activity.types';

type MergeTripsModalProps = {
  open: boolean;
  onClose: () => void;
  selectedTrip: Trip;
  onSuccess?: () => void;
};

const MergeTripsModal: FC<MergeTripsModalProps> = ({ open, onClose, selectedTrip, onSuccess }) => {
  const { data: allTrips, isLoading: isLoadingTrips } = useGetTripsQuery({});
  const [mergeTrips, { isLoading: isMerging }] = useMergeTripsMutation();
  const [previewMergeTrips, { isLoading: isPreviewLoading }] = usePreviewMergeTripsMutation();
  const [updateTripStop] = useUpdateTripStopMutation();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTripIds, setSelectedTripIds] = useState<number[]>([]);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [showMergeReview, setShowMergeReview] = useState(false);
  const [previewResult, setPreviewResult] = useState<MergeTripPreviewResponse | null>(null);
  const [discardStopIds, setDiscardStopIds] = useState<Set<number>>(new Set());
  const [pendingScheduleByStopId, setPendingScheduleByStopId] = useState<
    Record<number, { stopType?: string; startIso: string; endIso: string }>
  >({});
  const pendingScheduleRef = useRef(pendingScheduleByStopId);
  pendingScheduleRef.current = pendingScheduleByStopId;
  const [isFlushAndMerging, setIsFlushAndMerging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [mergeSuccessOpen, setMergeSuccessOpen] = useState(false);

  const parseApiError = (err: unknown): { status?: number; message?: string } => {
    if (!err || typeof err !== 'object') {
      return {};
    }

    const status = 'status' in err && typeof err.status === 'number' ? err.status : undefined;
    const data =
      'data' in err && err.data && typeof err.data === 'object'
        ? (err.data as { message?: string; detail?: string })
        : undefined;

    return {
      status,
      message: data?.message || data?.detail,
    };
  };

  useEffect(() => {
    if (open) {
      setMergeSuccessOpen(false);
    }
  }, [open]);

  const availableTrips = useMemo(() => {
    if (!allTrips) return [];
    return allTrips.filter(trip => trip.id !== selectedTrip.id);
  }, [allTrips, selectedTrip.id]);

  const filteredTrips = useMemo(() => {
    if (!searchQuery) return availableTrips;

    const query = searchQuery.toLowerCase();
    return availableTrips.filter(trip => {
      const name = trip.name.toLowerCase();
      const countries = trip.destination_countries
        .map(c => c.name)
        .join(', ')
        .toLowerCase();

      return name.includes(query) || countries.includes(query);
    });
  }, [availableTrips, searchQuery]);

  const selectedTripForDisplay = useMemo(() => {
    if (!allTrips?.length) return selectedTrip;
    return allTrips.find(t => t.id === selectedTrip.id) ?? selectedTrip;
  }, [allTrips, selectedTrip]);

  const tripNamesById = useMemo(() => {
    const map: Record<number, string> = {};
    const primary = selectedTripForDisplay;
    map[primary.id] = primary.name;
    selectedTripIds.forEach(id => {
      const trip = allTrips?.find(t => t.id === id);
      if (trip) map[id] = trip.name;
    });
    return map;
  }, [selectedTripForDisplay, selectedTripIds, allTrips]);

  const mergeRange = useMemo(() => {
    const mergeCandidates = [selectedTrip, ...(allTrips?.filter(trip => selectedTripIds.includes(trip.id)) ?? [])];

    if (mergeCandidates.length === 0) {
      return {
        startDate: selectedTrip.start_date,
        endDate: selectedTrip.end_date,
      };
    }

    const minStart = mergeCandidates.reduce(
      (min, trip) => (new Date(trip.start_date).getTime() < new Date(min).getTime() ? trip.start_date : min),
      mergeCandidates[0].start_date
    );
    const maxEnd = mergeCandidates.reduce(
      (max, trip) => (new Date(trip.end_date).getTime() > new Date(max).getTime() ? trip.end_date : max),
      mergeCandidates[0].end_date
    );

    return {
      startDate: minStart,
      endDate: maxEnd,
    };
  }, [allTrips, selectedTrip, selectedTripIds]);

  const handleTripToggle = (tripId: number) => {
    setSelectedTripIds(prev => (prev.includes(tripId) ? prev.filter(id => id !== tripId) : [...prev, tripId]));
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  const handleMergeClick = () => {
    if (selectedTripIds.length === 0) return;
    setError(null);
    setShowConfirmation(true);
  };

  const resetReviewState = () => {
    setShowMergeReview(false);
    setPreviewResult(null);
    setDiscardStopIds(new Set());
    setPendingScheduleByStopId({});
  };

  const completeMergeSuccess = () => {
    setMergeSuccessOpen(true);
    setSelectedTripIds([]);
    setSearchQuery('');
    setError(null);
    setShowConfirmation(false);
    resetReviewState();
    onSuccess?.();
    onClose();
  };

  const handleConfirmMerge = async () => {
    setError(null);
    try {
      const preview = await previewMergeTrips({
        tripId: selectedTrip.id,
        mergeTrips: selectedTripIds,
      }).unwrap();

      setShowConfirmation(false);

      if (!mergePreviewHasConflicts(preview)) {
        try {
          await mergeTrips({
            tripId: selectedTrip.id,
            mergeTrips: selectedTripIds,
          }).unwrap();
          completeMergeSuccess();
        } catch (mergeErr: unknown) {
          const { status, message } = parseApiError(mergeErr);

          if (status === 409) {
            setPreviewResult(applyPendingSchedulesToPreview(preview, pendingScheduleRef.current));
            setDiscardStopIds(new Set());
            setShowMergeReview(true);
            setError(message || 'Conflicts were found. Review and resolve them before finalizing merge.');
            return;
          }

          setError(message || 'Failed to merge trips');
        }
        return;
      }

      setPreviewResult(applyPendingSchedulesToPreview(preview, pendingScheduleRef.current));
      setDiscardStopIds(new Set());
      setShowMergeReview(true);
    } catch (err: unknown) {
      const { message } = parseApiError(err);
      setError(message || 'Could not validate merge. Please try again.');
      setShowConfirmation(false);
    }
  };

  const handleResolveConflictPair = (keepStopId: number, discardStopId: number) => {
    setDiscardStopIds(prev => {
      const next = new Set(prev);
      next.delete(keepStopId);
      next.add(discardStopId);
      return next;
    });
  };

  const handleKeepBothInPair = (leftId: number, rightId: number) => {
    setDiscardStopIds(prev => {
      const next = new Set(prev);
      next.delete(leftId);
      next.delete(rightId);
      return next;
    });
  };

  const refreshMergePreview = async (): Promise<MergeTripPreviewResponse | null> => {
    try {
      return await previewMergeTrips({
        tripId: selectedTrip.id,
        mergeTrips: selectedTripIds,
      }).unwrap();
    } catch {
      return null;
    }
  };

  const flushPendingSchedulePatches = async () => {
    const entries = Object.entries(pendingScheduleByStopId);
    if (entries.length === 0) return;

    for (const [idStr, pending] of entries) {
      const stopId = Number(idStr);
      const patchBody = getSchedulePatch(pending.stopType, pending.startIso, pending.endIso);
      const activityType = ((pending.stopType || 'Activity').trim() || 'Activity') as ActivityType;

      await updateTripStop({
        id: stopId,
        tripId: selectedTrip.id.toString(),
        activityType,
        data: patchBody as Record<string, unknown>,
      }).unwrap();
    }
  };

  const handleFinalizeMergeWithReview = async (discardStopIdsFromWizard?: number[]) => {
    setError(null);
    setIsFlushAndMerging(true);
    try {
      if (discardStopIdsFromWizard !== undefined) {
        setDiscardStopIds(new Set(discardStopIdsFromWizard));
      }
      await flushPendingSchedulePatches();
      const discardIds = discardStopIdsFromWizard !== undefined ? discardStopIdsFromWizard : Array.from(discardStopIds);
      await mergeTrips({
        tripId: selectedTrip.id,
        mergeTrips: selectedTripIds,
        discard_stop_ids: discardIds.length > 0 ? discardIds : undefined,
      }).unwrap();
      setPendingScheduleByStopId({});
      completeMergeSuccess();
    } catch (err: unknown) {
      const { message } = parseApiError(err);
      const fresh = await refreshMergePreview();

      if (fresh && mergePreviewHasConflicts(fresh)) {
        setPreviewResult(applyPendingSchedulesToPreview(fresh, pendingScheduleRef.current));
        setShowMergeReview(true);
        setError(
          message || 'Merge could not complete. Conflicts were updated — please resolve them and confirm again.'
        );
        return;
      }

      setError(message || 'Failed to merge trips');
    } finally {
      setIsFlushAndMerging(false);
    }
  };

  const getSchedulePatch = (stopType: string | undefined, startIso: string, endIso: string) => {
    const type = (stopType || '').trim() as ActivityType | '';
    if (type === 'Hotel') {
      return {
        check_in_date_time: startIso,
        check_out_date_time: endIso,
      };
    }

    const transportTypes: ActivityType[] = ['Flight', 'Ferry', 'Cruise', 'Rail', 'Transportation'];
    if (transportTypes.includes(type as ActivityType)) {
      return {
        departure_date_time: startIso,
        arrival_date_time: endIso,
      };
    }

    return {
      start_date_time: startIso,
      end_date_time: endIso,
    };
  };

  const queueStopScheduleChange = useCallback(
    (params: { stopId: number; stopType?: string; startIso: string; endIso: string }) => {
      setPendingScheduleByStopId(prev => ({
        ...prev,
        [params.stopId]: {
          stopType: params.stopType,
          startIso: params.startIso,
          endIso: params.endIso,
        },
      }));
      setPreviewResult(prev => syncPreviewStopTime(prev, params.stopId, params.startIso, params.endIso));
    },
    []
  );

  const syncPreviewStopTime = (
    preview: MergeTripPreviewResponse | null,
    stopId: number,
    startIso: string,
    endIso: string
  ): MergeTripPreviewResponse | null => {
    if (!preview) {
      return preview;
    }

    const isObject = (value: unknown): value is Record<string, unknown> =>
      Boolean(value) && typeof value === 'object' && !Array.isArray(value);

    const looksLikeStop = (value: Record<string, unknown>) =>
      'stop_type' in value ||
      'start' in value ||
      'end' in value ||
      'start_time' in value ||
      'end_time' in value ||
      'start_date_time' in value ||
      'end_date_time' in value ||
      'departure_date_time' in value ||
      'arrival_date_time' in value ||
      'check_in_date_time' in value ||
      'check_out_date_time' in value;

    const updateNode = (node: unknown): unknown => {
      if (Array.isArray(node)) {
        const next = node.map(updateNode);
        return next;
      }

      if (!isObject(node)) {
        return node;
      }

      let nextNode: Record<string, unknown> = node;
      let changed = false;

      if (typeof node.id === 'number' && node.id === stopId && looksLikeStop(node)) {
        nextNode = {
          ...nextNode,
          start: startIso,
          end: endIso,
          start_time: startIso,
          end_time: endIso,
          start_date_time: startIso,
          end_date_time: endIso,
          departure_date_time: startIso,
          arrival_date_time: endIso,
          check_in_date_time: startIso,
          check_out_date_time: endIso,
        };
        changed = true;
      }

      Object.keys(nextNode).forEach(key => {
        const child = nextNode[key];
        const updatedChild = updateNode(child);
        if (updatedChild !== child) {
          if (!changed) {
            nextNode = { ...nextNode };
            changed = true;
          }
          nextNode[key] = updatedChild;
        }
      });

      return nextNode;
    };

    return updateNode(preview) as MergeTripPreviewResponse;
  };

  const applyPendingSchedulesToPreview = (
    preview: MergeTripPreviewResponse | null,
    pending: Record<number, { stopType?: string; startIso: string; endIso: string }>
  ): MergeTripPreviewResponse | null => {
    if (!preview || Object.keys(pending).length === 0) {
      return preview;
    }
    let acc: MergeTripPreviewResponse | null = preview;
    for (const [idStr, p] of Object.entries(pending)) {
      acc = syncPreviewStopTime(acc, Number(idStr), p.startIso, p.endIso);
    }
    return acc;
  };

  const handleMergeReviewBack = () => {
    setError(null);
    resetReviewState();
  };

  const handleClose = () => {
    onClose();
    setSelectedTripIds([]);
    setSearchQuery('');
    setError(null);
    setShowConfirmation(false);
    resetReviewState();
  };

  const handleCancelConfirmation = () => {
    setShowConfirmation(false);
  };

  const getSelectedTripsText = () => {
    if (selectedTripIds.length === 0 || !allTrips) return '';

    return selectedTripIds
      .map(id => allTrips.find(t => t.id === id)?.name)
      .filter(Boolean)
      .join(', ');
  };

  const isMainMergeBusy = isMerging;

  return (
    <>
      <Modal
        open={open && !showMergeReview}
        onClose={handleClose}
        aria-labelledby="merge-trips-modal"
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(34, 43, 58, 0.2)',
          },
        }}
      >
        <Box className={styles.modalContainer}>
          <Box className={styles.modalHeader}>
            <IconButton
              onClick={handleClose}
              className={styles.backButton}
            >
              <ArrowBackIcon />
            </IconButton>

            <Typography
              variant="h5Bold"
              className={styles.headerTitle}
            >
              Merge Trips
            </Typography>

            <IconButton
              onClick={handleClose}
              className={styles.closeButton}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box className={styles.modalContent}>
            <Box className={styles.selectedTripSection}>
              <Typography
                variant="h9Bold"
                className={styles.sectionTitle}
              >
                Selected Trip
              </Typography>
              <Box className={styles.selectedTripCard}>
                <MergeTripCard
                  trip={selectedTripForDisplay}
                  variant="selected"
                  isSelected={false}
                  onToggle={() => {}}
                />
              </Box>
            </Box>

            <Box className={styles.mergeWithSection}>
              <Typography
                variant="h9Bold"
                className={styles.sectionTitle}
              >
                Merge with Trip
              </Typography>

              <Box className={styles.searchContainer}>
                <CustomInput
                  inputName="search"
                  inputLabel=""
                  placeholder="Search"
                  value={searchQuery}
                  onChange={handleSearchChange}
                  startIcon={
                    <SearchIcon
                      size={20}
                      color="#5E6A81"
                    />
                  }
                />
              </Box>

              <Box className={styles.tripsList}>
                {isLoadingTrips ? (
                  <Box className={styles.emptyState}>
                    <Typography
                      variant="h10"
                      sx={{ color: '#5E6A81' }}
                    >
                      Loading trips...
                    </Typography>
                  </Box>
                ) : filteredTrips.length === 0 ? (
                  <Box className={styles.emptyState}>
                    <Typography
                      variant="h10"
                      sx={{ color: '#5E6A81' }}
                    >
                      {searchQuery ? 'No trips found matching your search' : 'No trips available for merge'}
                    </Typography>
                  </Box>
                ) : (
                  <List>
                    {filteredTrips.map(trip => (
                      <ListItem
                        key={trip.id}
                        className={styles.tripItem}
                        onClick={() => handleTripToggle(trip.id)}
                      >
                        <ListItemText>
                          <MergeTripCard
                            trip={trip}
                            variant="selectable"
                            isSelected={selectedTripIds.includes(trip.id)}
                            onToggle={() => handleTripToggle(trip.id)}
                          />
                        </ListItemText>
                      </ListItem>
                    ))}
                  </List>
                )}
              </Box>
            </Box>

            {error && (
              <Typography
                variant="h10"
                sx={{ color: '#C8102E', textAlign: 'center', mt: 2 }}
              >
                {error}
              </Typography>
            )}
          </Box>

          <Box className={styles.modalFooter}>
            <CustomButton
              variant="outlined"
              onClick={handleClose}
              disabled={isMainMergeBusy}
            >
              Cancel
            </CustomButton>

            <CustomButton
              variant="contained"
              onClick={handleMergeClick}
              disabled={selectedTripIds.length === 0 || isMainMergeBusy}
            >
              {isMainMergeBusy ? 'Merging...' : 'Merge Trips'}
            </CustomButton>
          </Box>
        </Box>
      </Modal>

      <Modal
        open={showConfirmation && !showMergeReview}
        onClose={handleCancelConfirmation}
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(34, 43, 58, 0.35)',
          },
        }}
      >
        <Box className={styles.mergeConfirmPaper}>
          <Box className={styles.mergeConfirmHeader}>
            <ArrowBackIosIcon
              onClick={handleCancelConfirmation}
              sx={{ color: '#5E6A81' }}
            />
            <Box sx={{ flex: 1 }} />
            <Box
              onClick={handleCancelConfirmation}
              sx={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }}
              role="button"
              aria-label="Close"
            >
              <CloseIcon />
            </Box>
          </Box>

          <Box className={styles.mergeConfirmContent}>
            <Typography
              variant="h5Bold"
              className={styles.mergeConfirmTitle}
            >
              Merge Trips?
            </Typography>

            <Typography
              variant="h10"
              className={styles.mergeConfirmDesc}
            >
              Activities and travel buddies from &quot;{getSelectedTripsText()}&quot; will be merged with &quot;
              {selectedTrip.name}&quot;.
            </Typography>
          </Box>

          {isPreviewLoading && (
            <Typography
              variant="h10"
              sx={{ color: '#5E6A81', textAlign: 'center', mt: 1 }}
            >
              Checking for conflicts...
            </Typography>
          )}

          <Box className={styles.mergeConfirmActions}>
            <CustomButton
              variant="outlined"
              customClass={styles.mergeConfirmPill}
              onClick={handleCancelConfirmation}
              disabled={isPreviewLoading || isMerging}
            >
              Cancel
            </CustomButton>
            <CustomButton
              variant="contained"
              customClass={styles.mergeConfirmPill}
              onClick={handleConfirmMerge}
              disabled={isPreviewLoading || isMerging}
            >
              {isPreviewLoading ? 'Checking...' : isMerging ? 'Merging...' : 'Merge Trips'}
            </CustomButton>
          </Box>
        </Box>
      </Modal>

      {previewResult && (
        <MergeTripReviewDialog
          open={showMergeReview}
          targetTripName={selectedTrip.name}
          tripNamesById={tripNamesById}
          tripStartDate={mergeRange.startDate}
          tripEndDate={mergeRange.endDate}
          preview={previewResult}
          onResolvePair={handleResolveConflictPair}
          onKeepBothInPair={handleKeepBothInPair}
          onBack={handleMergeReviewBack}
          onFinalize={handleFinalizeMergeWithReview}
          onQueueScheduleChange={queueStopScheduleChange}
          isSubmitting={isMerging || isFlushAndMerging}
          error={error}
        />
      )}

      <Snackbar
        open={mergeSuccessOpen}
        autoHideDuration={5000}
        onClose={() => setMergeSuccessOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setMergeSuccessOpen(false)}
          severity="success"
          sx={{ width: '100%' }}
        >
          Trips merged successfully.
        </Alert>
      </Snackbar>
    </>
  );
};

export default MergeTripsModal;
